import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { useRef, useEffect } from "react";

const AnimatedCounter = ({ target, suffix = "", decimals = 0, delay = 0 }: { target: number; suffix?: string; decimals?: number; delay?: number }) => {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => decimals > 0 ? v.toFixed(decimals) : Math.floor(v).toString());
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      const timeout = setTimeout(() => {
        animate(count, target, { duration: 2, ease: "easeOut" });
      }, delay * 1000);
      return () => clearTimeout(timeout);
    }
  }, [isInView, target, count, delay]);

  return (
    <span ref={ref} className="tabular-nums">
      <motion.span>{rounded}</motion.span>{suffix}
    </span>
  );
};

const Stats = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const stats = [
    { value: 10, suffix: "+", label: "Jahre Erfahrung", decimals: 0 },
    { value: 200, suffix: "+", label: "Beratungen", decimals: 0 },
    { value: 100, suffix: " %", label: "AVGS-Förderung", decimals: 0 },
    { value: 5.0, suffix: "", label: "Google-Bewertung", decimals: 1 },
  ];

  return (
    <section className="relative py-20 bg-primary overflow-hidden">
      <div className="absolute inset-0 bg-dot-pattern opacity-10" />
      <motion.div
        className="absolute top-0 right-0 w-96 h-96 bg-purple-accent/15 rounded-full blur-[100px]"
        animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-0 left-0 w-72 h-72 bg-accent/15 rounded-full blur-[80px]"
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" ref={ref}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              className="text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
            >
              <div className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-2">
                <AnimatedCounter target={stat.value} suffix={stat.suffix} decimals={stat.decimals} delay={index * 0.15} />
              </div>
              <p className="text-primary-foreground/70 text-sm md:text-base font-medium tracking-wide uppercase">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;
